import axios from 'axios';
import { Fact } from '../types';
import { Category } from '../store/slices/categoriesSlice';

// 創建 axios 實例
const api = axios.create({
  // baseURL: 'http://192.168.31.9:3000/api', // 根據你的後端 URL 調整
  // baseURL: 'http://localhost:3000/api', // 根據你的後端 URL 調整
  baseURL: 'https://triviabackend-c9408953b256.herokuapp.com/api', // 根據你的後端 URL 調整
  timeout: 5000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 後端 API 響應類型轉換為前端類型
const convertTriviaToFact = (trivia: any): Fact => {
  return {
    id: trivia.id.toString(),
    title: trivia.title,
    content: trivia.content,
    category: mapCategoryName(trivia.category_name),
    source: trivia.source_url,
    likes: 0, // 後端可能會返回 likes_count
    likedBy: [],
    date: trivia.created_at,
    createdBy: trivia.author_name,
    status: trivia.status,
  };
};

const mapCategoryName = (categoryName: string): string => {
  const categoryMap: Record<string, string> = {
    自然與生物: 'nature',
    人體與心理: 'human',
    歷史與文化: 'history',
    科學與宇宙: 'science',
    科技與網路: 'tech',
    食物與日常生活: 'food',
    世界奇觀與旅遊趣事: 'travel',
    建築與城市冷知識: 'architecture',
    法律與規則的怪事: 'law',
    機率與巧合: 'probability',
  };

  return categoryMap[categoryName] || 'others';
};

// API 函數
export const triviaApi = {
  // 獲取隨機冷知識
  getRandomTrivia: async (userId?: string): Promise<Fact> => {
    try {
      const params = userId ? { userId } : {};
      const response = await api.get('/trivia/random', { params });

      if (response.data.status === 'success') {
        return convertTriviaToFact(response.data.data);
      }

      throw new Error(response.data.message || '獲取隨機冷知識失敗');
    } catch (error) {
      console.error('獲取隨機冷知識失敗:', error);
      throw error;
    }
  },

  // 獲取所有冷知識
  getAllTrivia: async (): Promise<Fact[]> => {
    try {
      const response = await api.get('/trivia');

      if (response.data.status === 'success') {
        return response.data.data.map(convertTriviaToFact);
      }

      throw new Error(response.data.message || '獲取冷知識列表失敗');
    } catch (error) {
      console.error('獲取冷知識列表失敗:', error);
      throw error;
    }
  },

  // 獲取特定冷知識
  getTriviaById: async (id: string): Promise<Fact> => {
    try {
      const response = await api.get(`/trivia/${id}`);

      if (response.data.status === 'success') {
        return convertTriviaToFact(response.data.data);
      }

      throw new Error(response.data.message || '獲取冷知識詳情失敗');
    } catch (error) {
      console.error('獲取冷知識詳情失敗:', error);
      throw error;
    }
  },

  // 獲取多個隨機冷知識
  getRandomTriviaList: async (
    count: number = 5,
    userId?: string
  ): Promise<Fact[]> => {
    try {
      const params: Record<string, string> = { count: count.toString() };
      if (userId) params.userId = userId;

      const response = await api.get('/trivia/random-list', { params });

      if (response.data.status === 'success') {
        return response.data.data.map(convertTriviaToFact);
      }

      throw new Error(response.data.message || '獲取隨機冷知識列表失敗');
    } catch (error) {
      console.error('獲取隨機冷知識列表失敗:', error);
      throw error;
    }
  },

  // 按類別獲取冷知識
  getTriviaByCategory: async (
    categoryId: string,
    page: number = 1,
    pageSize: number = 6,
    sortBy: string = 'date'
  ): Promise<{ facts: Fact[]; total: number; totalPages: number }> => {
    try {
      const params = { page, pageSize, sortBy };
      const response = await api.get(`/trivia/category/${categoryId}`, {
        params,
      });

      if (response.data.status === 'success') {
        return {
          facts: response.data.data.map(convertTriviaToFact),
          total: response.data.total,
          totalPages: response.data.totalPages,
        };
      }

      throw new Error(response.data.message || '獲取類別冷知識失敗');
    } catch (error) {
      console.error('獲取類別冷知識失敗:', error);
      throw error;
    }
  },
  // 在 triviaApi 對象中添加
  getAllCategories: async (): Promise<Category[]> => {
    try {
      const response = await api.get('/categories');

      if (response.data.status === 'success') {
        return response.data.data;
      }

      throw new Error(response.data.message || '獲取類別列表失敗');
    } catch (error) {
      console.error('獲取類別列表失敗:', error);
      throw error;
    }
  },
};
